import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addPreliminaryInquiry,
  addPreliminaryInquiryWitness,
} from "services/incidentService";
import {
  CrimeReference,
  Investigation,
  PreliminaryInquiry,
  Witness,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  action: "Add" | "Edit";
  investigation: Investigation;
  incident?: CrimeReference;
  preliminaryInquiry?: PreliminaryInquiry;
  allPreliminaries: PreliminaryInquiry[] | [];
  witness: Witness;
  onClose: (refetch: boolean) => void;
};

const defaultValues: PreliminaryInquiry = {
  uu_id: 0,
  prosecutor_registration_number: "",
  police_registration_number: "",
  reason_for_preliminary_inquiry: "",
  court_name: "",
  body_receiving_the_copy: "",
  inquiry_date: new Date(),
  findings: "",
  investigation: null,
  applying_prosecutor: "",
};

const PreliminaryForm: FC<Props> = ({
  action,
  investigation,
  incident,
  preliminaryInquiry,
  allPreliminaries,
  witness,
  onClose,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<PreliminaryInquiry>(
    preliminaryInquiry ?? defaultValues
  );
  const [loading, setLoading] = useState(false);
  const [theirStatement, setTheirStatement] = useState("");

  useEffect(() => {
    if (preliminaryInquiry && (investigation || incident)) {
      setFormState(preliminaryInquiry);
    }
  }, [preliminaryInquiry, investigation, incident]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.reason_for_preliminary_inquiry)
      return "Reason For Preliminary Inquiry is Required.";
    else if (!formState.court_name) return "Court Name is Required.";
    else if (!formState.body_receiving_the_copy)
      return "Body Receiving the Copy is Required.";
    else if (!formState.findings) return "Findings is Required.";

    return "";
  };

  const addToPreliminaryInquiryWitness = async (uu_id: number) => {
    addPreliminaryInquiryWitness({
      witness_info: witness?.uu_id,
      preliminary_inquiry: uu_id,
      their_statement: theirStatement,
      uu_id: 0,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success"
        );
        setFormState(defaultValues);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error"
        );
      });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    formState?.uu_id
      ? addToPreliminaryInquiryWitness(formState?.uu_id)
      : addPreliminaryInquiry({
          ...formState,
          investigation: investigation ? investigation?.uu_id : null,
          prosecutor_registration_number: investigation?.prosecutor?.uu_id
            ? investigation?.prosecutor?.uu_id?.toString()
            : "",
          police_registration_number: investigation?.police?.uu_id
            ? investigation?.police?.uu_id?.toString()
            : "",
          applying_prosecutor: loginData?.full_name || "",
        })
          .then((result: any) => {
            if (result?.uu_id) {
              addToPreliminaryInquiryWitness(result?.uu_id);
            }
          })
          .catch((error) => {
            showTooltip(
              error?.response?.data?.detail || errorHandlerOnForm(error),
              "error"
            );
          })
          .finally(() => {
            setLoading(false);
          });
  };

  return (
    <div>
      <div className="w-full">
        <h2 className="mb-1 text-2xl font-semibold text-[#E90101]">
          {action} Preliminary Request
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D] max-w-sm">
          Lorem Ipsum is a placeholder text commonly used in the design and
          publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Use Old Preliminary Request or Create a New One
            </label>
            <select
              className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
              value={formState?.uu_id}
              onChange={(e) => {
                const selectedPreliminary = allPreliminaries.find(
                  (preliminary) =>
                    preliminary.uu_id?.toString() === e.target.value
                );

                if (selectedPreliminary) {
                  // setTheirStatement(selectedPreliminary?.their_statement);
                  setFormState((prevState) => ({
                    ...prevState,
                    ...selectedPreliminary,
                  }));
                } else {
                  setTheirStatement("");
                  setFormState(defaultValues);
                }
              }}
            >
              <option value={""}>New Preliminary Request</option>
              {allPreliminaries.map((prosecutors, index) => (
                <option key={index} value={prosecutors.uu_id}>
                  {prosecutors.court_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Name
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 disabled:bg-transparent"
                disabled={!!formState?.uu_id}
                name="court_name"
                value={formState?.court_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Body Receiving the Copy
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 disabled:bg-transparent"
                disabled={!!formState?.uu_id}
                name="body_receiving_the_copy"
                value={formState?.body_receiving_the_copy}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Inquiry Date
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 disabled:bg-transparent"
                disabled={!!formState?.uu_id}
                name="inquiry_date"
                value={
                  formState?.inquiry_date
                    ? new Date(formState.inquiry_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Findings
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 disabled:bg-transparent"
                disabled={!!formState?.uu_id}
                name="findings"
                value={formState?.findings}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Reason for Preliminary Inquiry
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 disabled:bg-transparent"
                disabled={!!formState?.uu_id}
                name="reason_for_preliminary_inquiry"
                value={formState?.reason_for_preliminary_inquiry}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Their Statement
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                value={theirStatement}
                onChange={(e) => setTheirStatement(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end my-4 mb-4">
            <button
              type="submit"
              className={`border border-[#E90101] bg-[#E90101] px-4 py-2 
                        text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-white hover:text-[#E90101]
                            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Request for Preliminary Inquiry"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreliminaryForm;
